import { getDevices } from "./api/users_{userId}_devices";
import { sortedSetAdd } from "./sorted_set";
import * as Storage from "./storage";

/** @type {NodeJS.Timeout|undefined} */
let loadDevicesTimeout;

/** @type {Record<string, FarmcamSim>} */
const farmcamSims = {};

/** @type {Record<string, Fencealarm>} */
const fencealarms = {};

/** @type {Record<string, Fencealarm2>} */
const fencealarm2s = {};

/** @type {Record<string, Smartplug>} */
const smartplugs = {};

/** @type {Record<string, Smartplug2>} */
const smartplug2s = {};

/**
 * @returns {Promise<User>}
 */
async function getActiveUser() {
	const impersonate = sessionStorage.getItem("impersonate");
	if (impersonate) return JSON.parse(impersonate);
	return getSignedInUser();
}

/**
 * @returns {string[]}
 */
function getDeviceTags() {
	/** @type {Set<string>} */
	const tagSet = new Set();

	Object.entries(farmcamSims)
		.flatMap(([_imsi, fcs]) => fcs.Metadata.Tags)
		.forEach((tag) => {
			if (tag) tagSet.add(tag);
		});

	Object.entries(fencealarms)
		.flatMap(([_imei, fa]) => fa.Metadata.Tags)
		.forEach((tag) => {
			if (tag) tagSet.add(tag);
		});

	Object.entries(fencealarm2s)
		.flatMap(([_imei, fa2]) => fa2.Metadata.Tags)
		.forEach((tag) => {
			if (tag) tagSet.add(tag);
		});

	Object.entries(smartplugs)
		.flatMap(([_imei, sp]) => sp.Metadata.Tags)
		.forEach((tag) => {
			if (tag) tagSet.add(tag);
		});

	Object.entries(smartplug2s)
		.flatMap(([_imei, sp2]) => sp2.Metadata.Tags)
		.forEach((tag) => {
			if (tag) tagSet.add(tag);
		});

	return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
}

/**
 * @returns {Promise<User>}
 */
async function getSignedInUser() {
	const user = await Storage.getItem("user");
	if (user) return JSON.parse(user);
	return {
		Id: "",
		CustomerId: "",
		AccountType: "",
		Email: "",
		Phone: "",
		Locale: "en-GB",
		Country: "",
		EmailNotifications: {
			Billing: false,
			Device: false,
			Marketing: false,
		},
		PushNotifications: {
			Enabled: false,
			Tokens: [],
		},
		SmsNotifications: {
			Enabled: false,
			Balance: 0,
		},
	};
}

/**
 * @returns {Promise<Token>}
 */
async function getToken() {
	const token = await Storage.getItem("token");
	if (token) return JSON.parse(token);
	else
		return {
			String: "",
			ExpiresAt: "1970-01-01T00:00:00Z",
		};
}

/**
 * @returns {Promise<string>}
 */
async function getTokenString() {
	const token = await getToken();
	return token.String;
}

function isImpersonating() {
	const impersonate = sessionStorage.getItem("impersonate");
	if (impersonate) return true;
	return false;
}

async function isSignedIn() {
	const margin = 1e4;
	const expiresAt = new Date((await getToken()).ExpiresAt);
	const now = Date.now();
	return now < expiresAt.getTime() - margin;
}

async function loadDevices() {
	const devices = await getDevices();

	Object.keys(farmcamSims).forEach((imsi) => {
		delete farmcamSims[imsi];
	});

	for (const fcs of devices.FarmcamSims) {
		fcs.Metadata.Tags = sortedSetAdd(fcs.Metadata.Tags, "FarmCam SIM");
		fcs.Metadata.Tags = sortedSetAdd(
			fcs.Metadata.Tags,
			fcs.Metadata.OwnerEmail,
		);
		farmcamSims[fcs.Imsi] = fcs;
	}

	Object.keys(fencealarms).forEach((imei) => {
		delete fencealarms[imei];
	});

	for (const fa of devices.Fencealarms) {
		fa.Metadata.Tags = sortedSetAdd(fa.Metadata.Tags, "FenceAlarm");
		fa.Metadata.Tags = sortedSetAdd(
			fa.Metadata.Tags,
			fa.Metadata.OwnerEmail,
		);
		fencealarms[fa.Imei] = fa;
	}

	Object.keys(fencealarm2s).forEach((imei) => {
		delete fencealarm2s[imei];
	});

	for (const fa2 of devices.Fencealarm2s) {
		fa2.Metadata.Tags = sortedSetAdd(fa2.Metadata.Tags, "FenceAlarm2");
		fa2.Metadata.Tags = sortedSetAdd(
			fa2.Metadata.Tags,
			fa2.Metadata.OwnerEmail,
		);
		fencealarm2s[fa2.Imei] = fa2;
	}

	Object.keys(smartplugs).forEach((imei) => {
		delete smartplugs[imei];
	});

	for (const sp of devices.Smartplugs) {
		sp.Metadata.Tags = sortedSetAdd(sp.Metadata.Tags, "SmartPlug");
		sp.Metadata.Tags = sortedSetAdd(
			sp.Metadata.Tags,
			sp.Metadata.OwnerEmail,
		);
		smartplugs[sp.Imei] = sp;
	}

	Object.keys(smartplug2s).forEach((imei) => {
		delete smartplug2s[imei];
	});

	for (const sp2 of devices.Smartplug2s) {
		sp2.Metadata.Tags = sortedSetAdd(sp2.Metadata.Tags, "SmartPlug2");
		sp2.Metadata.Tags = sortedSetAdd(
			sp2.Metadata.Tags,
			sp2.Metadata.OwnerEmail,
		);
		smartplug2s[sp2.Imei] = sp2;
	}
}

/**
 * @param {Token} token
 */
async function setToken(token) {
	await Storage.setItem("token", JSON.stringify(token));
}

/**
 * @param {User} user
 */
async function setUser(user) {
	await Storage.setItem("user", JSON.stringify(user));
}

async function signOut() {
	await Storage.removeItem("token");
	await Storage.removeItem("user");
}

/**
 * @param {User} user
 */
async function startImpersonating(user) {
	sessionStorage.setItem("impersonate", JSON.stringify(user));
	await loadDevices();
}

/**
 * @param {() => void} onLoad
 */
function startLoadDevicesInterval(onLoad) {
	if (loadDevicesTimeout) return;
	const job = () => loadDevices().then(onLoad);
	loadDevicesTimeout = setInterval(job, 5000);
}

async function stopImpersonate() {
	sessionStorage.removeItem("impersonate");
	await loadDevices();
}

function stopLoadDevicesInterval() {
	clearInterval(loadDevicesTimeout);
	loadDevicesTimeout = undefined;
}

export const state = {
	farmcamSims,
	fencealarms,
	fencealarm2s,
	getActiveUser,
	getDeviceTags,
	getSignedInUser,
	getToken,
	getTokenString,
	isImpersonating,
	isSignedIn,
	loadDevices,
	setToken,
	setUser,
	signOut,
	smartplugs,
	smartplug2s,
	startImpersonating,
	startLoadDevicesInterval,
	stopImpersonate,
	stopLoadDevicesInterval,
};
